import React, { useState, useRef } from 'react';
import { Youtube, Play, Plus, Link as LinkIcon, CheckCircle2, Film, TrendingUp, X, Video, UploadCloud, Instagram, ExternalLink } from 'lucide-react';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

const VlogsPage = () => {
  const { isDark } = useTheme(); // 👈 Access Theme State
  const [isYoutubeConnected, setIsYoutubeConnected] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  
  // Modal Form States
  const [uploadType, setUploadType] = useState('link'); 
  const [newVlogUrl, setNewVlogUrl] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [vlogTitle, setVlogTitle] = useState('');
  const [socials, setSocials] = useState({ instagram: '', youtube: '' });
  
  const fileInputRef = useRef(null);
  
  // Official Odessey Videos + Community Videos
  const [vlogs, setVlogs] = useState([
    { 
      id: 1, 
      title: "🏔 From 11,200 ft to 14,640 ft ", 
      author: "Dipti Vyas", 
      type: "official", 
      views: "1.5K", 
      thumbnail: "https://himalayanambition.in/wp-content/uploads/2023/09/Gomukh-Trek-Tapovan.jpg",
      duration: "00:49",
      socials: { instagram: "odesseytravelstories" },
      videoUrl: "https://www.instagram.com/reel/DUnM_2EinHG/"
    },
    { 
      id: 2, 
      title: "A Glimpse of Udaipur - The City of Lakes 🌅", 
      author: "Odessey Official", 
      type: "official", 
      views: "1K", 
      thumbnail: "https://s7ap1.scene7.com/is/image/incredibleindia/jaisamand-lake-udaipur-rajasthan-city-ff?qlt=82&ts=1742188836501",
      duration: "00:50",
      socials: { instagram: "odesseytravelstories" },
      videoUrl: "https://www.instagram.com/reel/DVxl7JvCo-u/" 
    },
    { 
      id: 3, 
      title: "India's one of the finest Fairs - Surajkund Mela 2026", 
      author: "Odessey Official", 
      type: "official", 
      views: "1.3K", 
      thumbnail: "https://haryanatourism.gov.in/wp-content/uploads/2024/08/Visitors-throng-the-Mela-scaled.jpg",
      duration: "00:45",
      socials: { youtube: "odesseytravelstories" },
      videoUrl: "https://www.instagram.com/reel/DXiySjdkcSZ/" 
    },
    { 
      id: 4, 
      title: "Wait for the beast...", 
      author: "Karan Kothari", 
      type: "community", 
      views: "3K", 
      thumbnail: "https://s7ap1.scene7.com/is/image/incredibleindia/ranthambore-national-park-bharatpur-rajasthan-1-attr-hero?qlt=82&ts=1751458858841",
      duration: "00:35",
      socials: { instagram: "karanveerkothari" },
      videoUrl: "https://www.instagram.com/karanveerkothari/reel/DaVfTBTTiZ4/"  
    }
  ]);

  const handleConnectYoutube = () => setIsYoutubeConnected(true);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setVideoFile(file);
  };

  const handleAddVlog = (e) => {
    e.preventDefault();
    if (uploadType === 'link' && !newVlogUrl) return;
    
    const newVlog = {
      id: Date.now(),
      title: uploadType === 'upload' ? vlogTitle : "New Added Reel",
      author: "You", 
      type: "community", 
      views: "0",
      thumbnail: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=800&q=80",
      duration: "00:45",
      videoUrl: newVlogUrl || "#", 
      socials: {
        instagram: socials.instagram.replace('@', ''),
        youtube: socials.youtube.replace('@', '')
      }
    };

    setVlogs([newVlog, ...vlogs]);
    setNewVlogUrl('');
    setVideoFile(null);
    setVlogTitle('');
    setSocials({ instagram: '', youtube: '' });
    setShowAddModal(false);
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    
    if (url.includes('youtube.com/watch?v=')) {
      return url.replace('watch?v=', 'embed/').split('&')[0];
    } else if (url.includes('youtu.be/')) {
      return url.replace('youtu.be/', 'youtube.com/embed/').split('?')[0];
    } else if (url.includes('instagram.com/')) {
      const match = url.match(/\/(p|reel)\/([A-Za-z0-9_-]+)/);
      if (match) {
        const type = match[1]; 
        const id = match[2];   
        return `https://www.instagram.com/${type}/${id}/embed/`;
      }
    }
    return url;
  };

  const officialVlogs = vlogs.filter(v => v.type === 'official');
  const communityVlogs = vlogs.filter(v => v.type === 'community');

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', paddingBottom: '80px', transition: 'background-color 0.3s' }}>
      
      {/* 1. HERO SECTION */}
      <div style={{ position: 'relative', height: '35vh', minHeight: '300px', backgroundColor: isDark ? '#030712' : '#111827', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.unsplash.com/photo-1516934571997-6a4a6e4fbac0?auto=format&fit=crop&w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.4 }} />
        <div style={{ position: 'absolute', inset: 0, background: isDark ? 'linear-gradient(to top, #030712, transparent)' : 'linear-gradient(to top, #111827, transparent)' }} />
        
        <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '0 16px', maxWidth: '800px', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
            <Film size={28} color="#16a34a" />
            <h1 style={{ fontSize: 'clamp(32px, 5vw, 42px)', fontWeight: '900', color: 'white', margin: 0, textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>Odessey Vlogs</h1>
          </div>
          <p style={{ fontSize: '15px', color: '#d1d5db', lineHeight: '1.6' }}>
            Immerse yourself in cinematic travel experiences. Watch official guides or share your own vertical reels and expedition videos with the community.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 16px', boxSizing: 'border-box' }}>
        
        {/* 2. ADD & SYNC BANNER */}
        <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', padding: '24px 20px', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginBottom: '40px', border: isYoutubeConnected ? '2px solid #16a34a' : (isDark ? '1px solid #374151' : '1px solid #e5e7eb'), transition: 'background-color 0.3s, border-color 0.3s', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ width: '50px', height: '50px', borderRadius: '14px', backgroundColor: isYoutubeConnected ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#450a0a' : '#fee2e2'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Youtube size={28} color={isYoutubeConnected ? '#16a34a' : '#ef4444'} />
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: '0 0 4px 0' }}>
                {isYoutubeConnected ? 'YouTube Channel Synced' : 'Connect your YouTube Channel'}
              </h2>
              <p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0, maxWidth: '500px', lineHeight: '1.5' }}>
                Grant Odessey permission to display your travel vlogs directly on your profile. Grow your audience and inspire others!
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', width: window.innerWidth < 768 ? '100%' : 'auto' }}>
            <button 
              onClick={() => setShowAddModal(true)}
              style={{ width: window.innerWidth < 768 ? '100%' : 'auto', padding: '12px 20px', borderRadius: '12px', backgroundColor: isDark ? '#374151' : '#f3f4f6', color: isDark ? '#f9fafb' : '#4b5563', fontWeight: 'bold', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.2s' }}
              onMouseEnter={(e)=>e.currentTarget.style.backgroundColor= isDark ? '#4b5563' : '#e5e7eb'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor= isDark ? '#374151' : '#f3f4f6'}
            >
              <Plus size={16} /> Add Video Link
            </button>
          </div>
        </div>

        {/* 3. ODESSEY ORIGINALS SECTION */}
        <div style={{ marginBottom: '50px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp color="#16a34a" size={22} /> Odessey Originals
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {officialVlogs.map(vlog => (
              <VlogCard key={vlog.id} vlog={vlog} onPlay={() => setActiveVideo(vlog)} isDark={isDark} />
            ))}
          </div>
        </div>

        {/* 4. COMMUNITY VLOGS SECTION */}
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Video color="#0284c7" size={22} /> Community & Creator Reels
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {communityVlogs.map(vlog => (
              <VlogCard key={vlog.id} vlog={vlog} onPlay={() => setActiveVideo(vlog)} isDark={isDark} />
            ))}
          </div>
        </div>

      </div>

      {/* --- CINEMATIC VIDEO POP-UP MODAL --- */}
      {activeVideo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999999, backgroundColor: 'rgba(0, 0, 0, 0.9)', backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px', animation: 'fadeIn 0.3s ease-out', boxSizing: 'border-box' }}>
          
          <button 
            onClick={() => setActiveVideo(null)} 
            style={{ position: 'absolute', top: '15px', right: '20px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', transition: 'background 0.2s', zIndex: 10 }}
            onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.2)'} 
            onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.1)'}
          >
            <X size={20} />
          </button>
          
          <div style={{ 
            width: '100%', 
            maxWidth: activeVideo.videoUrl.includes('instagram') ? '380px' : '850px', 
            height: activeVideo.videoUrl.includes('instagram') ? '70vh' : '450px',
            backgroundColor: '#111827', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', position: 'relative'
          }}>
            {activeVideo.videoUrl.includes('instagram') ? (
              <iframe 
                src={getEmbedUrl(activeVideo.videoUrl)} 
                style={{ width: '100%', height: '100%', border: 'none' }} 
                scrolling="no" 
                allowTransparency="true" 
                allow="encrypted-media"
                title={activeVideo.title}
              />
            ) : (
              <iframe 
                src={getEmbedUrl(activeVideo.videoUrl)} 
                style={{ width: '100%', height: '100%', border: 'none' }} 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
                title={activeVideo.title}
              />
            )}
          </div>

          <div style={{ textAlign: 'center', marginTop: '15px', padding: '0 10px' }}>
            <h3 style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', margin: '0 0 4px 0' }}>{activeVideo.title}</h3>
            <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 12px 0' }}>By {activeVideo.author}</p>
            
            <a 
              href={activeVideo.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', 
                backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: '50px', 
                textDecoration: 'none', fontSize: '13px', fontWeight: 'bold', transition: 'background 0.2s', 
                border: '1px solid rgba(255,255,255,0.2)' 
              }}
              onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.2)'}
              onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.1)'}
            >
              {activeVideo.videoUrl.includes('instagram') ? <Instagram size={14} /> : <Youtube size={14} />}
              Watch on {activeVideo.videoUrl.includes('instagram') ? 'Instagram' : 'YouTube'}
              <ExternalLink size={12} style={{ marginLeft: '4px', opacity: 0.7 }} />
            </a>
          </div>
        </div>
      )}
      
      {/* ADD VLOG MODAL */}
      {showAddModal && (
         <div style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: 'rgba(17, 24, 39, 0.8)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
         <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', padding: '25px 20px', width: '100%', maxWidth: '450px', position: 'relative', animation: 'slideUp 0.3s ease-out', maxHeight: '90vh', overflowY: 'auto', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }} className="hide-scrollbar">
           <button onClick={() => setShowAddModal(false)} style={{ position: 'absolute', top: '15px', right: '15px', background: isDark ? '#374151' : '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isDark ? 'white' : '#111827' }}>
             <X size={16} />
           </button>
           
           <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: isDark ? '#f9fafb' : '#111827' }}>
             <Video color="#16a34a" size={20} /> Add Video Link
           </h2>
           
           <div style={{ display: 'flex', backgroundColor: isDark ? '#111827' : '#f3f4f6', borderRadius: '10px', padding: '3px', marginBottom: '20px' }}>
             <button 
               type="button"
               onClick={() => setUploadType('link')}
               style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', backgroundColor: uploadType === 'link' ? (isDark ? '#1f2937' : 'white') : 'transparent', color: uploadType === 'link' ? '#ef4444' : (isDark ? '#9ca3af' : '#6b7280'), boxShadow: uploadType === 'link' ? '0 2px 5px rgba(0,0,0,0.05)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
             >
               <LinkIcon size={14} /> Link URL
             </button>
             <button 
               type="button"
               onClick={() => setUploadType('upload')}
               style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', backgroundColor: uploadType === 'upload' ? (isDark ? '#1f2937' : 'white') : 'transparent', color: uploadType === 'upload' ? '#0284c7' : (isDark ? '#9ca3af' : '#6b7280'), boxShadow: uploadType === 'upload' ? '0 2px 5px rgba(0,0,0,0.05)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
             >
               <UploadCloud size={14} /> Upload File
             </button>
           </div>

           <form onSubmit={handleAddVlog} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
             
             {uploadType === 'link' ? (
               <div>
                 <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Instagram Reel or YouTube URL</label>
                 <div style={{ position: 'relative' }}>
                   <LinkIcon size={16} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                   <input 
                     type="url" 
                     placeholder="https://www.instagram.com/reel/..." 
                     value={newVlogUrl}
                     onChange={(e) => setNewVlogUrl(e.target.value)}
                     required 
                     style={{ width: '100%', boxSizing: 'border-box', padding: '12px 12px 12px 38px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '14px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} 
                   />
                 </div>
               </div>
             ) : (
               <>
                 <div>
                   <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Video Title</label>
                   <input 
                     type="text" 
                     placeholder="e.g. My Road Trip to Spiti Valley" 
                     value={vlogTitle}
                     onChange={(e) => setVlogTitle(e.target.value)}
                     required 
                     style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '14px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} 
                   />
                 </div>
                 <div>
                   <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Upload Video File</label>
                   <div 
                     onClick={() => fileInputRef.current.click()}
                     style={{ 
                      width: '100%', 
                      boxSizing: 'border-box',
                      padding: '20px 15px',
                      border: isDark ? '2px dashed #4b5563' : '2px dashed #cbd5e1', 
                      borderRadius: '10px', 
                      backgroundColor: isDark ? '#111827' : '#f8fafc', 
                      textAlign: 'center', 
                      cursor: 'pointer', 
                      transition: 'all 0.2s' 
                    }}
                >
                    <UploadCloud size={24} color="#0284c7" style={{ margin: '0 auto 6px' }} />
                    <p style={{ margin: 0, fontSize: '13px', color: isDark ? '#d1d5db' : '#4b5563', fontWeight: 'bold' }}>
                        {videoFile ? videoFile.name : 'Click to select MP4 or MOV'}
                     </p>
                  </div>
                  <input type="file" accept="video/*" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
                 </div>
               </>
             )}

             <div style={{ backgroundColor: isDark ? '#111827' : '#f9fafb', padding: '16px', borderRadius: '10px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxSizing: 'border-box' }}>
               <h4 style={{ fontSize: '13px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: '0 0 12px 0' }}>Promote Your Socials (Optional)</h4>
               
               <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                 <div style={{ position: 'relative' }}>
                   <Instagram size={14} color="#e1306c" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                   <input 
                     type="text" 
                     placeholder="@odesseytravelstories" 
                     value={socials.instagram}
                     onChange={(e) => setSocials({...socials, instagram: e.target.value})}
                     style={{ width: '100%', boxSizing: 'border-box', padding: '10px 10px 10px 36px', borderRadius: '8px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? 'white' : '#111827' }} 
                   />
                 </div>
                 <div style={{ position: 'relative' }}>
                   <Youtube size={14} color="#ef4444" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                   <input 
                     type="text" 
                     placeholder="YouTube Handle or URL" 
                     value={socials.youtube}
                     onChange={(e) => setSocials({...socials, youtube: e.target.value})}
                     style={{ width: '100%', boxSizing: 'border-box', padding: '10px 10px 10px 36px', borderRadius: '8px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? 'white' : '#111827' }} 
                   />
                 </div>
               </div>
             </div>

             <button type="submit" style={{ width: '100%', backgroundColor: '#16a34a', color: 'white', padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'background 0.2s', boxSizing: 'border-box' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#15803d'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#16a34a'}>
               Publish to Odessey
             </button>
           </form>
         </div>
       </div>
      )}

      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

const VlogCard = ({ vlog, onPlay, isDark }) => {
  return (
    <div 
      onClick={onPlay} 
      style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '18px', overflow: 'hidden', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: isDark ? '0 4px 10px rgba(0,0,0,0.3)' : '0 10px 25px rgba(0,0,0,0.03)', cursor: 'pointer', transition: 'transform 0.3s, background-color 0.3s, border-color 0.3s', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }} 
      onMouseEnter={(e)=>e.currentTarget.style.transform='translateY(-5px)'} 
      onMouseLeave={(e)=>e.currentTarget.style.transform='translateY(0)'}
    >
      <div style={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#111827' }}>
        <img src={vlog.thumbnail} alt={vlog.title} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }} />
        
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '45px', height: '45px', backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.4)' }}>
            <Play size={20} color="white" fill="white" style={{ marginLeft: '3px' }} />
          </div>
        </div>

        <div style={{ position: 'absolute', bottom: '8px', right: '8px', backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '11px', fontWeight: 'bold', padding: '3px 6px', borderRadius: '4px' }}>
          {vlog.duration}
        </div>
      </div>

      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: '0 0 8px 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {vlog.title}
        </h3>
        
        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <span style={{ fontWeight: '600', color: vlog.type === 'official' ? (isDark ? '#4ade80' : '#16a34a') : (isDark ? '#f9fafb' : '#111827'), display: 'flex', alignItems: 'center', gap: '4px' }}>
              {vlog.type === 'official' && <CheckCircle2 size={13} />} {vlog.author}
            </span>
            
            {vlog.socials && (
              <div style={{ display: 'flex', gap: '6px', marginTop: '5px', flexWrap: 'wrap' }}>
                {vlog.socials.instagram && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: '#e1306c', backgroundColor: isDark ? '#500724' : '#fdf2f8', padding: '2px 5px', borderRadius: '4px' }}>
                    <Instagram size={10} /> @{vlog.socials.instagram}
                  </span>
                )}
                {vlog.socials.youtube && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: '#ef4444', backgroundColor: isDark ? '#450a0a' : '#fef2f2', padding: '2px 5px', borderRadius: '4px' }}>
                    <Youtube size={10} /> {vlog.socials.youtube}
                  </span>
                )}
              </div>
            )}
          </div>
          
          <span style={{ fontWeight: '500' }}>{vlog.views} views</span>
        </div>
      </div>
    </div>
  );
};

export default VlogsPage;